// MUS-607: a box placed beside its target ('left' or 'right') only flipped to
// the other side when it ran off the screen; where neither side had room (an
// iPad in portrait, a target near the middle) it stayed cut off at the edge.
// Keep it inside the screen across as well, 8px from the edge.
//
// Keeping it inside the screen could then push it back over the control it
// explains (a phone in landscape: the lesson's Automatic Tempo Adjustment
// switch). When neither side has room, try below and then above the target
// first. A side counts as having room when the box fits across from the
// target; a box taller than the space above or below its middle still slides
// along that side as before, so boxes that already had room do not move.
//
// Where the target itself is off the screen (the repertoire piece's toolbar
// runs past the right edge of a phone held upright), the box followed it off
// the screen and NEXT could not be tapped. The box now stays on the screen
// even then (no tether).

// 350px wide, or narrower when the window is (a phone held upright): the
// window less the box's padding and border (34px), its margins (20px) and
// 8px of edge room each side.
export const TOUR_BOX_WIDTH = 'min(350px, calc(100vw - 70px))';

export type TourPlacement = 'left' | 'right' | 'top' | 'bottom';

export const TOUR_FALLBACK_PLACEMENTS: Record<TourPlacement, TourPlacement[]> = {
  left: ['right', 'bottom', 'top'],
  right: ['left', 'bottom', 'top'],
  top: ['bottom', 'right', 'left'],
  bottom: ['top', 'right', 'left'],
};

export const tourPopperModifiers = (
  offsetX: number,
  offsetY: number,
  placement: TourPlacement = 'right',
) => [
  { name: 'offset', options: { offset: [offsetX, offsetY] } },
  {
    name: 'flip',
    options: { fallbackPlacements: TOUR_FALLBACK_PLACEMENTS[placement], altAxis: false },
  },
  { name: 'preventOverflow', options: { altAxis: true, padding: 8, tether: false } },
];

// Where a tall target leaves room for the full box on neither side nor above
// or below it (the repertoire's accuracy panel at 844x390 and 667x375), every
// placement was cut off and Popper pushed the box back over about 100px of
// the panel. When the full box fits on neither side, a box placed beside its
// target now narrows to the room on the roomier side, down to
// TOUR_BOX_MIN_CONTENT_WIDTH_PX, and is placed there.
export const TOUR_BOX_CONTENT_WIDTH_PX = 350;
export const TOUR_BOX_MIN_CONTENT_WIDTH_PX = 220;
// 16px padding and a 1px border each side, the 10px margin each side and the
// 8px kept from the screen edge.
export const TOUR_BOX_CHROME_PX = 62;

export type TourBoxFit = { placement: TourPlacement; contentWidthPx: number | null };

type SideRoomTarget = { left: number; right: number };
type BesideSide = 'left' | 'right';

export const fitTourBoxBesideTarget = (
  placement: TourPlacement,
  target: SideRoomTarget,
  windowWidth: number,
): TourBoxFit => {
  if (placement === 'top' || placement === 'bottom') return { placement, contentWidthPx: null };
  const room: Record<BesideSide, number> = {
    left: target.left - TOUR_BOX_CHROME_PX,
    right: windowWidth - target.right - TOUR_BOX_CHROME_PX,
  };
  const opposite: BesideSide = placement === 'left' ? 'right' : 'left';
  const sides: BesideSide[] = [placement, opposite];
  const fullBoxFits = sides.some((side) => room[side] >= TOUR_BOX_CONTENT_WIDTH_PX);
  if (fullBoxFits) return { placement, contentWidthPx: null };
  const roomier = room[placement] >= room[opposite] ? placement : opposite;
  if (room[roomier] < TOUR_BOX_MIN_CONTENT_WIDTH_PX) return { placement, contentWidthPx: null };
  return { placement: roomier, contentWidthPx: Math.floor(room[roomier]) };
};
